import { isAfter, parseISO } from "date-fns";
import isBefore from "date-fns/isBefore";
import UISubscriptionStatusEnum from "./UISubscriptionStatusEnum";
import { SubscriptionType } from "@WahooFitness/cloud-client-types";
import SubscriptionStatus from "./CloudSubscriptionStatus";
import { getPendingSubscriptionChange } from "./getPendingSubscriptionChange";

function getUISubscriptionState(cloudSubscription: SubscriptionType): UISubscriptionStatusEnum {
  const now = new Date();
  const nowString = now.toISOString();
  const trialEndDate = parseISO(cloudSubscription?.trial_end || nowString);
  const currentPeriodEnd = parseISO(cloudSubscription?.current_period_end || nowString);
  const cancelled = !!cloudSubscription?.cancel_at || !!cloudSubscription?.canceled_at;
  const trialLapsed = isBefore(trialEndDate, now);
  const pendingSubscriptionChange = getPendingSubscriptionChange(cloudSubscription);
  const pendingChange = !!pendingSubscriptionChange?.id;
  const isIAP =
    cloudSubscription?.subscription_processor &&
    (cloudSubscription?.subscription_processor.name === "apple" ||
      cloudSubscription?.subscription_processor.name === "google");
  const renewal = !!cloudSubscription?.next_renewal_at;
  const compType = "complimentary";
  const hasCoupon = !!cloudSubscription?.subscription_coupon_redemption;

  const priority: { [value in UISubscriptionStatusEnum]: number } = {
    ImmediateIAPSubscription: 1,
    TrialExpiredNoSubscription: 2,
    CanceledSubscription: 3,
    PaymentError: 4,
    ActiveCancelledSubscription: 5,
    TrialAvailableNoSubscription: 6,
    TrialingTimeOffPromotion: 7,
    ActiveTimeOffPromotion: 8,
    HonorarySubscription: 9,
    HonoraryRecurringSubscription: 10,
    PriceCoupon: 11,
    ActiveSubscription: 12,
    ActiveSubscriptionWithPendingDowngrade: 13,
    OnTrialFutureSubscription: 14,
    OnIapTrial: 15,
    OnTrialNoSubscription: 16,
  };

  type statusFunc = () => boolean | UISubscriptionStatusEnum;

  const subscriptionStates: {
    [key in SubscriptionStatus]: statusFunc[];
  } = {
    trialing: [
      () =>
        // Trial expired
        !pendingChange && trialLapsed && UISubscriptionStatusEnum.TrialExpiredNoSubscription,
      () =>
        // Trialing but processor is apple or google the user actually has an active subscription
        isIAP ? UISubscriptionStatusEnum.OnIapTrial : false,
      () =>
        // Trialing with future sub
        pendingChange && UISubscriptionStatusEnum.OnTrialFutureSubscription,
      () =>
        // Trialing with no future sub
        !pendingChange && UISubscriptionStatusEnum.OnTrialNoSubscription,
      () =>
        // Honorary due to pending plan change
        cloudSubscription?.subscription_pending_change?.plan_type?.toLocaleLowerCase() === compType
          ? UISubscriptionStatusEnum.HonorarySubscription
          : false,
      () =>
        // Magento
        isAfter(currentPeriodEnd, trialEndDate) &&
        !pendingChange &&
        !renewal &&
        hasCoupon &&
        UISubscriptionStatusEnum.PriceCoupon,
      () =>
        // A lapsed trial is always TrialExpiredNoSubscription, even if cloud sync never
        // populated plan_code for the trial itself — don't let it masquerade as an active IAP.
        !trialLapsed &&
        !cloudSubscription?.plan_code &&
        (cloudSubscription?.subscription_processor?.name === "apple" ||
          cloudSubscription?.subscription_processor?.name === "google") &&
        UISubscriptionStatusEnum.ImmediateIAPSubscription,
      () =>
        !cloudSubscription?.subscription_coupon_redemption &&
        !pendingSubscriptionChange &&
        !renewal &&
        !!cloudSubscription?.current_period_start &&
        cloudSubscription?.subscription_processor?.name === "wahoo" &&
        cloudSubscription?.plan_type?.toLocaleLowerCase() !== compType &&
        UISubscriptionStatusEnum.TrialingTimeOffPromotion,
    ],
    active: [
      () =>
        // Honorary recurring sub on current plan
        cloudSubscription?.plan_type?.toLocaleLowerCase() === compType &&
        renewal &&
        UISubscriptionStatusEnum.HonoraryRecurringSubscription,
      () =>
        // Honorary non-recurring sub on current plan
        cloudSubscription?.plan_type?.toLocaleLowerCase() === compType &&
        !renewal &&
        UISubscriptionStatusEnum.HonorarySubscription,
      () =>
        // Honorary due to pending plan change
        cloudSubscription?.subscription_pending_change?.plan_type?.toLocaleLowerCase() === compType
          ? UISubscriptionStatusEnum.HonorarySubscription
          : false,
      () =>
        // Active with cancelled sub
        cancelled && UISubscriptionStatusEnum.ActiveCancelledSubscription,
      () =>
        // Magento
        cloudSubscription?.subscription_processor?.name === "wahoo" &&
        !renewal &&
        hasCoupon &&
        UISubscriptionStatusEnum.PriceCoupon,
      () =>
        // Active
        UISubscriptionStatusEnum.ActiveSubscription,
      // iap before webhook completes
      () =>
        !cloudSubscription?.plan_code &&
        (cloudSubscription?.subscription_processor?.name === "apple" ||
          cloudSubscription?.subscription_processor?.name === "google") &&
        UISubscriptionStatusEnum.ImmediateIAPSubscription,
      () =>
        !cloudSubscription?.subscription_coupon_redemption &&
        !pendingSubscriptionChange &&
        !renewal &&
        cloudSubscription?.subscription_processor?.name === "wahoo" &&
        cloudSubscription?.plan_type?.toLocaleLowerCase() !== compType &&
        UISubscriptionStatusEnum.ActiveTimeOffPromotion,
      () => hasCoupon && UISubscriptionStatusEnum.PriceCoupon,
    ],
    expired: [
      () =>
        // Expired with no trial
        trialLapsed && UISubscriptionStatusEnum.TrialExpiredNoSubscription,
      () =>
        // Expired with trial available
        !trialLapsed && UISubscriptionStatusEnum.TrialAvailableNoSubscription,
    ],
    cancelled: [
      () =>
        // Cancelled sub
        trialLapsed && UISubscriptionStatusEnum.CanceledSubscription,
      () =>
        // cancelled sub, but trial available
        !trialLapsed && UISubscriptionStatusEnum.TrialAvailableNoSubscription,
    ],
    failed_payment: [
      () =>
        // Failed payment
        UISubscriptionStatusEnum.PaymentError,
    ],
  };

  const statusFound = cloudSubscription?.status
    ? subscriptionStates[cloudSubscription?.status as SubscriptionStatus]
    : false;

  if (statusFound) {
    const [topStatus] = statusFound
      .map((statusFunc: statusFunc) => statusFunc())
      .filter((value): value is UISubscriptionStatusEnum => value && typeof value !== "boolean")
      .sort((a, b) => priority[a] - priority[b]);

    return topStatus;
  }

  return UISubscriptionStatusEnum.TrialAvailableNoSubscription;
}

export default getUISubscriptionState;
